import { makeAutoObservable, runInAction } from "mobx";
import type RootStore from "stores/RootStore";
import type { ServiceStoreType } from "../../../types/ServiceStoreTypes";
import { getOrThrow, loadErrorMessage } from "shared/loadOrError";
import { isSessionEnded } from "stores/sessionGuard";
import type { ClientEditAccess, ClientOverview } from "./clientOverview.types";

/**
 * Client 360's Overview — ONE read of `client-overview` (the API rolls a
 * parent's companies up and reads every section as the person asking).
 * Registered as `clientOverviewViewStore`; the ClientDetail route pins it to
 * the open client on enter and resets it on exit, like the Tickets tab's store.
 *
 * The overview itself lives in the `client-overview` ServiceStore (rule 1:
 * API entities only in ServiceStores), keyed by `clientId`. This store holds
 * which client is open and how its read went — nothing else.
 *
 * It also carries the API's answer to "may this person change this client?"
 * (the client write gate, #2613), which every Client 360 tab's write controls
 * follow through `ClientDetailStore.canWrite`. While it is unknown (not read
 * yet, or the read failed) the answer is null and the tabs fall back to the
 * role alone: the API refuses a change it does not allow whatever this page
 * shows, and a page that hid every control because one read failed would lock
 * out the people who may edit.
 */
export default class ClientOverviewStore {
  rootStore: RootStore;

  clientId: string | null = null;
  loading = false;
  error: string | null = null;

  private seq = 0;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  private get service(): ServiceStoreType | null {
    const root = this.rootStore as unknown as {
      serviceStoreManager?: { getStore?: (path: string) => unknown };
      clientOverviewStore?: unknown;
    };
    return ((root.serviceStoreManager?.getStore?.("client-overview") ?? root.clientOverviewStore) as ServiceStoreType | null) ?? null;
  }

  /** The open client's overview, from the ServiceStore — null until it is read. */
  get overview(): ClientOverview | null {
    const id = this.clientId?.toLowerCase();
    if (!id) return null;
    const entities = (this.service?.entities ?? []) as unknown as ClientOverview[];
    return entities.find((e) => String(e.clientId).toLowerCase() === id) ?? null;
  }

  get access(): ClientEditAccess | null {
    return this.overview?.access ?? null;
  }

  /** May this person change this client? true / false from the API, null while unknown. */
  get canEditClient(): boolean | null {
    const access = this.access;
    return access ? access.canEdit : null;
  }

  /** The API's sentence saying why this person may not change this client, or null. */
  get editRefusal(): string | null {
    const access = this.access;
    if (!access || access.canEdit) return null;
    return access.message || "You can see this client but not change it.";
  }

  /** Point the store at a client and read its overview. */
  async initializeForClient(clientId: string, options: { includeDeleted?: boolean } = {}): Promise<void> {
    this.clientId = clientId;
    await this.load(options);
  }

  /**
   * (Re)read the open client's overview. Never rejects: a failure lands on
   * `error`, so the page can say the overview could not be read instead of
   * showing empty cards.
   */
  async load(options: { includeDeleted?: boolean } = {}): Promise<void> {
    const clientId = this.clientId;
    if (!clientId) return;
    const seq = ++this.seq;
    this.loading = true;
    this.error = null;
    try {
      await getOrThrow(this.service, clientId, options.includeDeleted ? { query: { includeDeleted: true } } : {});
    } catch (err) {
      // Superseded by a newer read, or ended by a sign-out: not this read's failure.
      if (seq !== this.seq || isSessionEnded(err)) return;
      runInAction(() => {
        this.error = loadErrorMessage(err, "This client's overview could not be loaded.");
      });
    } finally {
      if (seq === this.seq) {
        runInAction(() => {
          this.loading = false;
        });
      }
    }
  }

  /** Leaving Client 360, and sign-out (RootStore.purgeUserData resets every app store). */
  reset() {
    this.seq += 1;
    this.clientId = null;
    this.loading = false;
    this.error = null;
  }
}
